import { ClinicalProtocolType } from "@/model/ClinicalProtocol"
import { Organization } from "@/model/Organization"
import {
  bloomlifeId,
  caregiverId,
  clinicalProtocolIndex,
  cuffSize,
  dateOfBirth,
  departmentId,
  deviceDistributionMethod,
  diagnosis,
  emailAddress,
  firstName,
  insuranceProvider,
  lastName,
  localDate,
  medicalRecordNumber,
  patientOptedInClinically,
  phoneNumber,
  preferredLocale,
  shippingAddress,
} from "@/schemas/zod-schemas"
import { supportalTimeZones } from "@/utils/timezoneOptions"
import { z } from "zod"

export const formSchema = ({
  organization,
  protocol,
}: {
  organization: Organization | undefined
  protocol: ClinicalProtocolType
}) =>
  z
    .object({
      medicalRecordNumber: z
        .string()
        .nonempty("Medical record number is a required field")
        .pipe(medicalRecordNumber),
      bloomlifeId: z
        .string()
        .nonempty("Bloomlife ID number is a required field")
        .pipe(bloomlifeId),
      firstName: z
        .string()
        .nonempty("First name is a required field")
        .pipe(firstName),
      lastName: z
        .string()
        .nonempty("Last name is a required field")
        .pipe(lastName),
      emailAddress: emailAddress.or(z.literal("")).optional(),
      phoneNumber: z
        .string()
        .nonempty("Phone number is a required field")
        .pipe(phoneNumber),
      dateOfBirth: dateOfBirth.or(z.literal("")).optional(),
      preferredLocale: preferredLocale.or(z.literal("")).optional(),
      timeZone: z
        .string()
        .nonempty("Time zone is a required field")
        .pipe(z.enum(supportalTimeZones)),
      shippingAddress: shippingAddress.optional(),
      insuranceProvider: insuranceProvider.or(z.literal("")).optional(),
      clinicalProtocolIndex: z.number().pipe(clinicalProtocolIndex),
      diagnosis: diagnosis.or(z.literal("")).optional(),
      startDate: z
        .string()
        .nonempty("Start date is a required field")
        .pipe(localDate),
      caregiverId: caregiverId.nonempty("Provider is a required field"),
      departmentId: departmentId.or(z.literal("")).optional(),
      estimatedDateOfDelivery: localDate.or(z.literal("")).optional(),
      deliveryDate: localDate.or(z.literal("")).optional(),
      patientOptedInClinically: patientOptedInClinically,
      deviceDistributionMethod: deviceDistributionMethod.optional(),
      cuffSize: cuffSize.optional(),
    })
    .refine(
      ({ deviceDistributionMethod }) =>
        protocol !== "diabetes" || deviceDistributionMethod,
      {
        message: "Device distribution is a required field",
        path: ["deviceDistributionMethod"],
      }
    )
    .refine(({ cuffSize }) => protocol !== "hypertension" || cuffSize, {
      message: "Cuff size is a required field",
      path: ["cuffSize"],
    })
    .refine(
      ({ departmentId }) =>
        !organization || organization.emrType !== "athena" || departmentId,
      {
        message: "Department is a required field",
        path: ["departmentId"],
      }
    )
